import CopyButton from "@/components/shared/form/copy_button";
import { bytesToStorageString, is_same_day } from "@/components/shared/utils";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useRouter } from "next/router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ServerLocationTile } from "../shared/product/server-location-tile";
import CardProgressBar from "./progress-bar";
export default function ProductOverviewCard({
	product,
	show_progress_bar = false,
	show_description = true,
	trans_key,
}) {
	return (
		<Grid item xs={12} lg={6} xl={4}>
			<Paper variant="outlined">
				<CardHeader product={product} trans_key={trans_key} />
				<CardBody product={product} show_description={show_description} trans_key={trans_key} />
				{show_progress_bar && <CardProgressBar trans_key={trans_key} product={product} />}
				<CardFooter product={product} trans_key={trans_key} />
			</Paper>
		</Grid>
	);
}

function CardHeader({ product, trans_key }) {
	const currentDate = new Date();
	const targetDate = new Date(product.created_at);
	const { t } = useTranslation();

	const transKeyCardBody = `${trans_key}.manage.overview.card.body`;

	return (
		<Paper
			variant="outlined"
			sx={{
				alignItems: "center",
				borderBottomLeftRadius: 0,
				borderBottomRightRadius: 0,
				display: "flex",
				position: "relative",
				px: 2,
				py: 1,
			}}
		>
			<Tooltip title={`${product.id} - ${product.name}`}>
				<Box display="flex" alignItems="center" width={{ md: "60%", xl: "65%", xs: "65%" }}>
					<BackupOutlinedIcon sx={{ color: "primary.main", fontSize: 32 }} />

					<Typography noWrap sx={{ fontSize: "16px", fontWeight: 500, ml: 1 }}>
						{product.id} - {product.name}
					</Typography>
				</Box>
			</Tooltip>

			<Box position="absolute" sx={{ right: 15 }}>
				{/* Show free version chip */}
				{product.is_free_version && (
					<Chip label={t(`${transKeyCardBody}.free_version`)} color="info" size="small" />
				)}
				{/* Show recently created chip */}
				{is_same_day(currentDate, targetDate) && (
					<Chip label={t(`${transKeyCardBody}.new`)} color="info" size="small" sx={{ ml: 1 }} />
				)}
			</Box>
		</Paper>
	);
}
function CardBody({ product, show_description = true, trans_key }) {
	const { t } = useTranslation();
	const countryCode = product.country_code;
	const country = t(`country_list.${countryCode.toUpperCase()}`);

	const storageString = product?.usage
		? bytesToStorageString(product.usage?.bytes_rounded)
		: undefined;

	const transKeyCardBody = `${trans_key}.manage.overview.card.body`;

	return (
		<Box sx={{ px: 2, py: 2 }}>
			<Grid container display="flex" alignItems="flex-start" rowSpacing={2.5} columnSpacing={2.5}>
				<CardBodyColumn
					title={t(`${transKeyCardBody}.hostname`)}
					content={
						<Box
							component="a"
							href={`https://${product.host_public ? product.host_public : product.host}`}
							target="_blank"
							display="flex"
							alignItems="center"
						>
							<Box component="span">{product.host_public ? product.host_public : product.host}</Box>
							<LaunchIcon sx={{ fontSize: "16px", ml: 0.5 }} />
						</Box>
					}
					allow_copy
				/>
				<CardBodyColumn title={t(`${transKeyCardBody}.name`)} content={product.name} allow_copy />
				<CardBodyColumn
					title={t(`${transKeyCardBody}.username`)}
					content={product.username}
					allow_copy
				/>
				<CardBodyColumn
					title={t(`${transKeyCardBody}.location`)}
					content={<ServerLocationTile product={product} />}
				/>

				{show_description === true && (
					<CardBodyColumn
						title={t(`${transKeyCardBody}.description`)}
						content={product.description ? product.description : " - "}
					/>
				)}

				{storageString && (
					<CardBodyColumn title={t(`${transKeyCardBody}.in_use`)} content={storageString} />
				)}
			</Grid>
		</Box>
	);
}

function CardBodyColumn({ title, content, allow_copy = false }) {
	return (
		<Grid item xs={6}>
			<Typography fontWeight={500}>{title}</Typography>

			<Box display="flex" alignItems="center">
				<Tooltip title={content}>
					<Typography noWrap>{content}</Typography>
				</Tooltip>

				{allow_copy && <CopyButton value={content} icon_sx={{ height: 20, width: 20 }} />}
			</Box>
		</Grid>
	);
}

function CardFooter({ product, trans_key }) {
	// Hooks
	const router = useRouter();
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();

	// Handlers
	const redirectHandler = () => {
		setLoading(true);
		router.push(`/${trans_key}/manage/${product.id}`);
	};

	return (
		<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5, px: 2, py: 1 }}>
			<LoadingButton
				loading={loading}
				loadingPosition="end"
				variant="outlined"
				endIcon={<ArrowForwardIcon />}
				onClick={redirectHandler}
				disableElevation
			>
				{t(`${trans_key}.manage.overview.card.footer.bttn`)}
			</LoadingButton>
		</Box>
	);
}
